import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PreviewSection from '../components/PreviewSection';
import sampleEventData from '../data/sampleEventData';
import { getEvent, getCustomUrlsByOrganizer } from '../services/firestoreService';
import NotFound from './NotFound';
import { formatTournamentDate } from '../components/DateSelector';
import { calculateNextOccurrence, formatRecurrencePattern, isRecurringEvent } from '../utils/recurrenceUtils';

const EventPreview = ({ eventId: propEventId }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customShareUrl, setCustomShareUrl] = useState(null);
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { eventId: paramEventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use eventId from props or URL params
  const eventId = propEventId || paramEventId;
  
  useEffect(() => {
    // Scroll to top when component mounts or eventId changes
    window.scrollTo(0, 0);
  }, [eventId]);
  
  // Clean up any registration success URL parameter (no longer needed since success modal is now in RegistrationForm)
  useEffect(() => {
    if (searchParams.get('registered') === 'success') {
      // Just clean up the URL parameter without showing modal
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('registered');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Show overlay on every visit when event data loads
  useEffect(() => {
    if (eventData) {
      setShowWelcomeOverlay(true);
    }
  }, [eventData]);

  // Disable/enable body scroll when overlay is shown/hidden
  useEffect(() => {
    if (showWelcomeOverlay) {
      // Disable scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showWelcomeOverlay]);
  
  useEffect(() => {
    const loadEvent = async () => {
      if (!eventId) {
        setError('No event ID provided');
        setLoading(false);
        return;
      }

      try {
        // First check if it's the static sample data
        if (eventId === 'SampleData0725') {
          setEventData(sampleEventData);
          setLoading(false);
          return;
        }

        // Try to load from Firebase
        const firebaseEvent = await getEvent(eventId);
        
        if (firebaseEvent) {
          // Only show published events
          if (firebaseEvent.status === 'published') {
            setEventData(firebaseEvent);
            // Fetch custom URL for this event
            if (firebaseEvent.organizerId) {
              try {
                const urls = await getCustomUrlsByOrganizer(firebaseEvent.organizerId);
                const eventUrl = urls.find(url => url.eventId === eventId);
                if (eventUrl) {
                  setCustomShareUrl(`https://chessweb.site/${eventUrl.urlExtension}`);
                } else {
                  setCustomShareUrl(null);
                }
              } catch (e) {
                setCustomShareUrl(null);
              }
            }
          } else {
            setError('Event not found or not published');
          }
        } else {
          setError('Event not found');
        }
      } catch (err) {
        setError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [eventId]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-chess-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !eventData) {
    return <NotFound />;
  }

  // Helper function to generate tournament description for meta tags
  const generateTournamentDescription = () => {
    let datePart = '';

    // Handle recurring events
    if (isRecurringEvent(eventData)) {
      const nextDate = calculateNextOccurrence(eventData);
      if (nextDate) {
        datePart = `${nextDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} (${formatRecurrencePattern(eventData)})`;
      } else {
        datePart = formatRecurrencePattern(eventData);
      }
    } else if (eventData.startDate) {
      // Handle one-time events
      datePart = formatEventDate(eventData.startDate, eventData.endDate, eventData.isSingleDay);
    }

    const locationPart = eventData.city || eventData.venueName || '';
    const description = eventData.eventDescription ?
      eventData.eventDescription.substring(0, 155) + '...' : '';

    if (datePart && locationPart) {
      return `Chess tournament on ${datePart} at ${locationPart}. ${description}`.trim();
    } else if (description) {
      return description;
    } else {
      return `${eventData.eventTitle} - Chess tournament hosted on ChessWeb.site`;
    }
  };
  
  // Use toggles from event data if available, otherwise show all sections
  const toggles = eventData.sections || {
    showHome: true,
    showLocation: !!eventData.location,
    showContactInfo: !!eventData.contactInfo,
    showRules: !!eventData.rules,
    showCustom: !!eventData.customName,
    showRegistration: !!eventData.registrationInfo,
    showFAQ: !!eventData.faqContent,
    showResults: !!eventData.resultsLink,
    showParticipants: !!eventData.participants?.length,
    showSchedule: !!eventData.scheduleLink,
    showAbout: !!eventData.eventDescription
  };

  // Helper to format date
  function formatEventDate(startDate, endDate, isSingleDay) {
    if (!startDate) return '';

    // Parse YYYY-MM-DD manually to avoid timezone conversion issues
    const parseDate = (dateString) => {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    };

    const start = parseDate(startDate);
    if (isNaN(start.getTime())) return '';
    if (isSingleDay || !endDate) {
      return start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    const end = parseDate(endDate);
    if (isNaN(end.getTime())) return start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    if (startDate === endDate) {
      return start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return `${start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
  }

  return (
    <div className="relative min-h-screen">
      {/* Fixed Background Image */}
      <div 
        className="fixed inset-0 w-full h-screen z-0"
        style={{
          background: `
            linear-gradient(45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%), 
            linear-gradient(-45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%), 
            linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%), 
            linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%),
            linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 90%)
          `,
          backgroundAttachment: 'fixed',
          boxShadow: 'inset 0 2px 12px rgba(12, 10, 10, 0.6)'
        }}
      />
      
      {/* Scrollable Content Overlay */}
      <div className="relative z-10 min-h-screen">
      {/* Dynamic Meta Tags for Tournament */}
      <Helmet>
        <title>{eventData.eventTitle} - ChessWeb.site</title>
        <meta name="title" content={`${eventData.eventTitle} - ChessWeb.site`} />
        <meta name="description" content={generateTournamentDescription()} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="event" />
        <meta property="og:url" content={customShareUrl || `https://chessweb.site/${eventData.eventId || eventData.id || eventId}`} />
        <meta property="og:title" content={`${eventData.eventTitle} - ChessWeb.site`} />
        <meta property="og:description" content={generateTournamentDescription()} />
        <meta property="og:image" content={eventData.socialShareImage || '/EventSocialSharing.png'} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={customShareUrl || `https://chessweb.site/${eventData.eventId || eventData.id || eventId}`} />
        <meta property="twitter:title" content={`${eventData.eventTitle} - ChessWeb.site`} />
        <meta property="twitter:description" content={generateTournamentDescription()} />
        <meta property="twitter:image" content={eventData.socialShareImage || '/EventSocialSharing.png'} />
      </Helmet>

      {/* Welcome Overlay */}
      {showWelcomeOverlay && (
        <div className={`fixed inset-0 w-screen h-screen z-50 flex items-center justify-center transition-all duration-1000 ease-out ${
          isClosing 
            ? 'transform scale-95 opacity-0' 
            : 'transform scale-100 opacity-100'
        }`}
        style={{
          background: isClosing 
            ? `linear-gradient(to bottom right, ${lightenColor(eventData.primaryColorHex || '#3B82F6', 0.3)}, #1e293b, #000000)`
            : `linear-gradient(to bottom right, #0f172a, ${lightenColor(eventData.primaryColorHex || '#3B82F6', 0.3)}, #000000)`
        }}>
          
          {/* Animated Chess Pattern Background */}
          <div className="absolute inset-0 opacity-10">
            <div className="grid grid-cols-8 h-full">
              {Array.from({ length: 64 }).map((_, i) => (
                <div 
                  key={i} 
                  className={`${(Math.floor(i / 8) + i) % 2 === 0 ? 'bg-white' : 'bg-transparent'} 
                    transform transition-all duration-2000 ${isClosing ? 'rotate-12 scale-110' : 'rotate-0 scale-100'}`}
                  style={{ animationDelay: `${i * 20}ms` }}
                />
              ))}
            </div>
          </div>
          

          {/* Main Content */}
          <div className={`text-center p-8 max-w-6xl mx-auto transform transition-all duration-800 ${
            isClosing ? 'translate-y-8 opacity-0' : 'translate-y-0 opacity-100'
          }`}>
            {/* Logo/Brand Area */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-2xl"
                style={{
                  background: `linear-gradient(to bottom right, ${eventData.primaryColorHex || '#3B82F6'}, ${eventData.primaryColorHex || '#3B82F6'}90)`
                }}>
                <i className="fas fa-chess-king text-3xl text-white"></i>
              </div>
              <div className="text-sm font-semibold tracking-widest uppercase mb-2 text-black">ChessWeb.Site</div>
            </div>

            {/* Main Heading */}
            <div className="mb-12">
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-4 leading-snug text-white">
                {eventData.eventTitle || 'Championship Tournament'}
              </h1>
              <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed italic font-semibold text-black">
               Your next move is waiting...
               <i class="fa-solid fa-chess-pawn" style={{ marginLeft: '5px' }}></i>
              </p>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => {
                setIsClosing(true);
                setTimeout(() => setShowWelcomeOverlay(false), 1000);
              }}
              className="group inline-flex items-center justify-center px-12 py-6 text-xl font-bold text-white rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300"
              style={{
                background: `linear-gradient(to right, ${eventData.primaryColorHex || '#3B82F6'}, ${eventData.primaryColorHex || '#3B82F6'}90)`,
                boxShadow: `0 0 30px ${eventData.primaryColorHex || '#3B82F6'}80, 0 0 60px ${eventData.primaryColorHex || '#3B82F6'}40, 0 8px 32px rgba(0,0,0,0.3)`
              }}
            >
              <span className="flex items-center space-x-3">
                <span>Enter Tournament</span>
                <span className="text-2xl group-hover:translate-x-1 transition-transform duration-300">→</span>
              </span>
            </button>

            {/* Elegant Divider */}
            <div className="mt-12 flex items-center justify-center space-x-4">
              <div className="w-16 h-px"
                style={{ background: `linear-gradient(to right, transparent, #000000)` }}></div>
              <div className="w-2 h-2 rounded-full"
                style={{ backgroundColor: '#000000' }}></div>
              <div className="w-16 h-px"
                style={{ background: `linear-gradient(to left, transparent, #000000)` }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Content */}
      <PreviewSection
        data={eventData}
        toggles={toggles}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Footer with share link */}
      <div className="bg-chess-cream border-t border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="mb-2">
              <p className="text-sm font-medium text-gray-700 mb-2">Share this tournament:</p>
              <div className="flex items-center justify-center space-x-3">
                <span className="text-xs text-gray-500 font-mono">
                  {customShareUrl || `https://chessweb.site/${eventData.eventId || eventData.id || eventId}`}
                </span>
                <button
                  onClick={() => {
                    const url = customShareUrl || `https://chessweb.site/${eventData.eventId || eventData.id || eventId}`;
                    navigator.clipboard.writeText(url);
                    // You could add a toast notification here
                  }}
                  className="px-3 py-1 text-xs bg-chess-green text-white hover:bg-chess-green/90 rounded transition-colors"
                >
                  COPY LINK
                </button>
              </div>
            </div>
            <div className="text-gray-500 font-light mb-2 flex items-center justify-center gap-1">
              Made with <a href="https://chessweb.site/tournaments" className="text-chess-green font-medium">ChessWeb.site</a>
              <img 
                src="/ChessWeb.site_Transparent.png" 
                alt="ChessWeb.Site Logo" 
                className="w-6 h-6"
              />
            </div>

            {/* <div className="flex items-center justify-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-500">
                Modified&nbsp;&nbsp;
                {eventData.updatedAt ? 
                (eventData.updatedAt.toDate ? 
                  eventData.updatedAt.toDate().toLocaleDateString() : 
                  new Date(eventData.lastModified || eventData.updatedAt).toLocaleDateString()) : 
                'N/A'}
              </span>
            </div> */}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// Helper function to lighten a hex color
function lightenColor(hex, amount = 0.2) {
  // Convert hex to RGB
  let c = hex.replace('#', '');
  if (c.length === 3) c = c.split('').map(x => x + x).join('');
  const num = parseInt(c, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.min(255, Math.floor(r + (255 - r) * amount));
  g = Math.min(255, Math.floor(g + (255 - g) * amount));
  b = Math.min(255, Math.floor(b + (255 - b) * amount));
  return `rgb(${r}, ${g}, ${b})`;
}

export default EventPreview; 